import type { EpisodeView, Id, ScriptDocument } from "@supermanus/document-model";
import { getNoteColumns, listEpisodes } from "@supermanus/document-model";

export type EditorScreen = "dashboard" | "episode";

/** Permalink to a production, episode tab, scene, and/or notes column. */
export type ShareLocation = {
  productionId: Id;
  episodeId?: Id;
  view?: EpisodeView;
  sceneId?: Id;
  noteColumnId?: Id;
};

export type ResolvedShareLocation = {
  productionId: Id;
  screen: EditorScreen;
  episodeId: Id | null;
  view: EpisodeView;
  sceneId: Id | null;
  noteColumnId: Id | null;
};

function workspaceView(view: EpisodeView): EpisodeView {
  return view === "home" ? "status" : view;
}

const VIEW_SLUG: Record<EpisodeView, string> = {
  timing: "sceneliste",
  outline: "rundown",
  script: "manus",
  sources: "noter",
  crew: "creditliste",
  programtext: "programtekst",
  status: "status",
  home: "status",
};

const VIEW_FROM_SLUG: Record<string, EpisodeView> = {
  sceneliste: "timing",
  timing: "timing",
  rundown: "outline",
  outline: "outline",
  manus: "script",
  script: "script",
  noter: "sources",
  sources: "sources",
  notes: "sources",
  creditliste: "crew",
  crew: "crew",
  programtekst: "programtext",
  programtext: "programtext",
  status: "status",
  home: "status",
};

const P_KEYS = ["produktion", "production", "p"];
const E_KEYS = ["episode", "e"];
const V_KEYS = ["fane", "view", "v"];
const S_KEYS = ["scene", "s"];
const N_KEYS = ["kolonne", "column", "note", "n"];

function firstParam(params: URLSearchParams, keys: string[]): string | null {
  for (const key of keys) {
    const value = params.get(key)?.trim();
    if (value) return value;
  }
  return null;
}

function parseView(raw: string | null): EpisodeView | undefined {
  if (!raw) return undefined;
  return VIEW_FROM_SLUG[raw.trim().toLowerCase()];
}

/** Read a share location from `window.location.search`. */
export function parseShareSearch(
  search: string = typeof window !== "undefined" ? window.location.search : "",
): ShareLocation | null {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  const productionId = firstParam(params, P_KEYS);
  if (!productionId) return null;
  const loc: ShareLocation = { productionId };
  const episodeId = firstParam(params, E_KEYS);
  const view = parseView(firstParam(params, V_KEYS));
  const sceneId = firstParam(params, S_KEYS);
  const noteColumnId = firstParam(params, N_KEYS);
  if (episodeId) loc.episodeId = episodeId;
  if (view) loc.view = view;
  if (sceneId) loc.sceneId = sceneId;
  if (noteColumnId) loc.noteColumnId = noteColumnId;
  return loc;
}

function sceneBelongsOnView(view: EpisodeView): boolean {
  const v = workspaceView(view);
  return v === "script" || v === "sources";
}

/** Query string without `?`. Dashboard links are production-only. */
export function serializeShareSearch(loc: ShareLocation): string {
  const params = new URLSearchParams();
  params.set("produktion", loc.productionId);
  if (loc.episodeId) {
    params.set("episode", loc.episodeId);
    const view = loc.view ? workspaceView(loc.view) : undefined;
    if (view) params.set("fane", VIEW_SLUG[view]);
    if (loc.sceneId && (!view || sceneBelongsOnView(view))) {
      params.set("scene", loc.sceneId);
    }
    if (loc.noteColumnId && (!view || workspaceView(view) === "sources")) {
      params.set("kolonne", loc.noteColumnId);
      if (!view) params.set("fane", VIEW_SLUG.sources);
    }
  }
  return params.toString();
}

export function pageUrlBase(): string {
  const url = new URL(window.location.href);
  url.search = "";
  url.hash = "";
  return url.toString();
}

export function buildShareUrl(base: string, loc: ShareLocation): string {
  const url = new URL(base, "https://minproduktion.dk");
  url.search = serializeShareSearch(loc);
  url.hash = "";
  return url.toString();
}

export function writeShareUrl(loc: ShareLocation | null, base = pageUrlBase()) {
  const next = loc ? buildShareUrl(base, loc) : base;
  if (window.location.href === next) return;
  window.history.replaceState(null, "", next);
}

/** Map a raw link onto ids that exist in this document. */
export function resolveShareLocation(
  doc: ScriptDocument,
  loc: ShareLocation,
): ResolvedShareLocation {
  const productionId = doc.production.id;
  const episodes = listEpisodes(doc);
  const scene = loc.sceneId ? doc.scenes[loc.sceneId] : undefined;
  const wantedEpisodeId = scene?.episodeId ?? loc.episodeId;
  const episode =
    (wantedEpisodeId && doc.episodes[wantedEpisodeId]) || episodes[0] || null;

  const openEpisode = Boolean(
    loc.episodeId || loc.view || loc.sceneId || loc.noteColumnId,
  );
  if (!openEpisode || !episode) {
    return {
      productionId,
      screen: "dashboard",
      episodeId: episode?.id ?? null,
      view: "timing",
      sceneId: episode?.sceneIds[0] ?? null,
      noteColumnId: null,
    };
  }

  let view: EpisodeView = loc.view
    ? workspaceView(loc.view)
    : loc.noteColumnId
      ? "sources"
      : loc.sceneId
        ? "script"
        : "timing";
  if (loc.noteColumnId) view = "sources";

  const sceneIds = episode.sceneIds.filter((id) => doc.scenes[id]);
  const sceneId =
    (loc.sceneId && sceneIds.includes(loc.sceneId) ? loc.sceneId : null) ??
    sceneIds[0] ??
    null;

  const columns = getNoteColumns(doc.production);
  const noteColumnId =
    view === "sources"
      ? (columns.some((c) => c.id === loc.noteColumnId)
          ? loc.noteColumnId!
          : (columns[0]?.id ?? null))
      : null;

  return {
    productionId,
    screen: "episode",
    episodeId: episode.id,
    view,
    sceneId,
    noteColumnId,
  };
}

export function editorStateToShareLocation(opts: {
  productionId: Id;
  screen: EditorScreen;
  episodeId: Id | null;
  view: EpisodeView;
  sceneId: Id | null;
  noteColumnId: Id | null;
}): ShareLocation {
  if (opts.screen !== "episode" || !opts.episodeId) {
    return { productionId: opts.productionId };
  }
  const view = workspaceView(opts.view);
  const loc: ShareLocation = {
    productionId: opts.productionId,
    episodeId: opts.episodeId,
    view,
  };
  if (sceneBelongsOnView(view) && opts.sceneId) loc.sceneId = opts.sceneId;
  if (view === "sources" && opts.noteColumnId) {
    loc.noteColumnId = opts.noteColumnId;
  }
  return loc;
}

export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const field = document.createElement("textarea");
    field.value = text;
    field.setAttribute("readonly", "");
    field.style.position = "fixed";
    field.style.left = "-9999px";
    document.body.appendChild(field);
    field.select();
    const ok = document.execCommand("copy");
    field.remove();
    return ok;
  } catch {
    return false;
  }
}

export async function copyShareUrl(
  base: string,
  loc: ShareLocation,
): Promise<boolean> {
  const url = buildShareUrl(base, loc);
  const ok = await copyText(url);
  if (ok) return true;
  window.prompt("Kopiér linket:", url);
  return false;
}
